import React, { useState, useEffect } from "react";
import { useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  Search,
  Check,
  X,
  Clock,
  User,
  Edit2,
  StickyNote,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { it, enUS } from "date-fns/locale";

const Appointments = () => {
  const { t, language } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedStaff, setSelectedStaff] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [editForm, setEditForm] = useState({
    date: "", time: "", staff_id: "", status: "", notes: "",
  });
  const [editSaving, setEditSaving] = useState(false);

  const statuses = [
    { value: "all", label: language === "it" ? "Tutti" : "All" },
    { value: "pending", label: t("pending") },
    { value: "awaiting_confirmation", label: t("awaiting_confirmation") },
    { value: "confirmed", label: t("confirmed") },
    { value: "unconfirmed", label: t("unconfirmed") },
    { value: "completed", label: t("completed") },
    { value: "cancelled", label: t("cancelled") },
    { value: "no_show", label: t("no_show") }
  ];

  useEffect(() => {
    fetchStaff();
    fetchServices();
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate, selectedStaff, selectedStatus]);

  const fetchStaff = async () => {
    try {
      const response = await axios.get(`${API}/staff`, { withCredentials: true });
      setStaff(response.data);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchServices = async () => {
    try {
      const r = await axios.get(`${API}/services`, { withCredentials: true });
      setServices((r.data || []).filter(s => s.is_active !== false));
    } catch { /* silent */ }
  };

  const openEditDialog = (appt) => {
    setEditingAppt(appt);
    setEditForm({
      date:     appt.date  || "",
      time:     appt.time  || "",
      staff_id: appt.staff_id || "",
      status:   appt.status || "pending",
      notes:    appt.notes  || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editingAppt) return;
    setEditSaving(true);
    try {
      await axios.patch(`${API}/appointments/${editingAppt.appointment_id}`, editForm, {
        withCredentials: true,
      });
      toast.success(language === "it" ? "Appuntamento aggiornato" : "Appointment updated");
      setEditOpen(false);
      setEditingAppt(null);
      fetchAppointments();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    } finally {
      setEditSaving(false);
    }
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const params = {
        date: format(selectedDate, "yyyy-MM-dd")
      };
      if (selectedStaff && selectedStaff !== "all") params.staff_id = selectedStaff;
      if (selectedStatus && selectedStatus !== "all") params.status = selectedStatus;
      
      const response = await axios.get(`${API}/appointments`, { 
        params,
        withCredentials: true 
      });
      setAppointments(response.data);
    } catch (error) {
      toast.error(language === "it" ? "Errore nel caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appointmentId, newStatus) => {
    try {
      await axios.patch(`${API}/appointments/${appointmentId}`, 
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(language === "it" ? "Stato aggiornato" : "Status updated");
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const cancelAppointment = async (appointmentId) => {
    if (!window.confirm(language === "it" ? "Annullare questo appuntamento?" : "Cancel this appointment?")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/appointments/${appointmentId}`, { withCredentials: true });
      toast.success(language === "it" ? "Appuntamento annullato" : "Appointment cancelled");
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  const getStatusBadge = (status) => {
    const classes = {
      pending: "badge-warning",
      awaiting_confirmation: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
      confirmed: "badge-info",
      unconfirmed: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
      completed: "badge-success",
      cancelled: "badge-error",
      no_show: "badge-error"
    };
    return classes[status] || "bg-muted";
  };

  const requestConfirmation = async (appointmentId) => {
    try {
      await axios.post(`${API}/appointments/${appointmentId}/request-confirmation`, {}, {
        withCredentials: true
      });
      toast.success(language === "it" ? "Richiesta di conferma inviata" : "Confirmation request sent");
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore" : "Error"));
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t("appointments")}</h1>
          <p className="text-muted-foreground mt-1">
            {language === "it" ? "Gestisci gli appuntamenti" : "Manage appointments"}
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start" data-testid="date-picker-btn">
                <CalendarIcon className="w-4 h-4 mr-2" />
                {format(selectedDate, "PPP", { locale: language === "it" ? it : enUS })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                locale={language === "it" ? it : enUS}
              />
            </PopoverContent>
          </Popover>

          <Select value={selectedStaff} onValueChange={setSelectedStaff}>
            <SelectTrigger className="w-[180px]" data-testid="staff-filter">
              <SelectValue placeholder={language === "it" ? "Operatore" : "Staff"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === "it" ? "Tutti" : "All"}</SelectItem>
              {staff.map((s) => (
                <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Appointments List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full"></div>
          </div>
        ) : appointments.length > 0 ? (
          <div className="space-y-3">
            {appointments.map((appt) => (
              <div
                key={appt.appointment_id}
                className="p-4 rounded-2xl border bg-card shadow-card hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                onClick={(e) => {
                  // Ignore clicks coming from action buttons
                  if (e.target.closest("button")) return;
                  openEditDialog(appt);
                }}
                data-testid={`appointment-${appt.appointment_id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-accent/10 flex flex-col items-center justify-center">
                      <span className="font-mono font-bold text-accent">{appt.time}</span>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold">{appt.client_name}</h3>
                      <p className="text-sm text-muted-foreground">{appt.service_name}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {appt.duration} min
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {appt.staff_name}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(appt.status)}`}>
                      {t(appt.status)}
                    </span>

                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => openEditDialog(appt)}
                      data-testid={`edit-appt-${appt.appointment_id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1" />
                      {language === "it" ? "Modifica" : "Edit"}
                    </Button>
                    {appt.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => requestConfirmation(appt.appointment_id)}
                          title={language === "it" ? "Richiedi conferma" : "Request confirmation"}
                        >
                          {language === "it" ? "Chiedi Conferma" : "Ask Confirm"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => updateStatus(appt.appointment_id, "confirmed")}
                          data-testid={`confirm-${appt.appointment_id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-destructive hover:text-destructive"
                          onClick={() => cancelAppointment(appt.appointment_id)}
                          data-testid={`cancel-${appt.appointment_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}

                    {appt.status === "awaiting_confirmation" && (
                      <span className="text-xs text-amber-600">
                        {language === "it" ? "In attesa di risposta cliente" : "Waiting for client response"}
                      </span>
                    )}

                    {appt.status === "unconfirmed" && (
                      <span className="text-xs text-orange-600">
                        {language === "it" ? "Cliente non ha confermato" : "Client didn't confirm"}
                      </span>
                    )}
                    
                    {appt.status === "confirmed" && (
                      <Button
                        size="sm"
                        className="rounded-full"
                        onClick={() => updateStatus(appt.appointment_id, "completed")}
                        data-testid={`complete-${appt.appointment_id}`}
                      >
                        {language === "it" ? "Completa" : "Complete"}
                      </Button>
                    )}
                  </div>
                </div>

                {appt.notes && (
                  <p className="mt-3 text-sm text-muted-foreground border-t pt-3">
                    {language === "it" ? "Note:" : "Notes:"} {appt.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {language === "it" ? "Nessun appuntamento per questa data" : "No appointments for this date"}
            </p>
          </div>
        )}
      </div>

      {/* ───── EDIT APPOINTMENT DIALOG ─────────────────────────── */}
      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) setEditingAppt(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-appt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-4 h-4 text-blue-600" />
              {language === "it" ? "Modifica appuntamento" : "Edit appointment"}
            </DialogTitle>
            <DialogDescription>
              {editingAppt ? (
                <>
                  <span className="font-bold">{editingAppt.client_name}</span>
                  {editingAppt.service_name && <span> · {editingAppt.service_name}</span>}
                </>
              ) : (
                language === "it"
                  ? "Modifica data, ora, operatore, stato e note."
                  : "Update date, time, staff, status and notes."
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {language === "it" ? "Data" : "Date"}
                </label>
                <Input
                  type="date"
                  value={editForm.date}
                  onChange={(e) => setEditForm(f => ({ ...f, date: e.target.value }))}
                  data-testid="edit-appt-date"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                  {language === "it" ? "Ora" : "Time"}
                </label>
                <Input
                  type="time"
                  value={editForm.time}
                  onChange={(e) => setEditForm(f => ({ ...f, time: e.target.value }))}
                  data-testid="edit-appt-time"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                {language === "it" ? "Operatore" : "Staff"}
              </label>
              <select
                value={editForm.staff_id}
                onChange={(e) => setEditForm(f => ({ ...f, staff_id: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm"
                data-testid="edit-appt-staff"
              >
                {staff.map(s => (
                  <option key={s.staff_id} value={s.staff_id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                {language === "it" ? "Stato" : "Status"}
              </label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm"
                data-testid="edit-appt-status"
              >
                <option value="pending">{t("pending")}</option>
                <option value="awaiting_confirmation">{t("awaiting_confirmation")}</option>
                <option value="confirmed">{t("confirmed")}</option>
                <option value="completed">{t("completed")}</option>
                <option value="cancelled">{t("cancelled")}</option>
                <option value="no_show">{t("no_show")}</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                <StickyNote className="w-3.5 h-3.5 text-amber-500" />
                {language === "it" ? "Note" : "Notes"}
              </label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))}
                rows={4}
                placeholder={language === "it"
                  ? "Aggiungi note interne sull'appuntamento (visibili solo allo staff)…"
                  : "Add internal notes about this appointment (visible only to staff)…"}
                className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                data-testid="edit-appt-notes"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {editForm.notes?.length || 0} {language === "it" ? "caratteri" : "chars"}
              </p>
            </div>

            {editingAppt?.service_name && (
              <div className="rounded-xl bg-slate-50 dark:bg-white/5 px-3 py-2 text-[11px] text-slate-500 flex items-center justify-between">
                <span>
                  <Clock className="w-3 h-3 inline mr-1" />
                  {editingAppt.duration || editingAppt.total_duration}m · {editingAppt.service_name}
                </span>
                <span className="font-mono opacity-60">#{editingAppt.appointment_id?.slice(-6)}</span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-appt-cancel">
              {t("cancel")}
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editSaving || !editForm.date || !editForm.time || !editForm.staff_id}
              className="bg-gradient-to-r from-blue-500 to-violet-500 text-white"
              data-testid="edit-appt-save"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {editSaving
                ? (language === "it" ? "Salvataggio…" : "Saving…")
                : (language === "it" ? "Salva modifiche" : "Save changes")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default Appointments;
